// ============================================================
// ExpressEnvConfig.jsx
//
// A branded, responsive course content page for the
// "Environment Variables & Configuration" lesson.
//
// Author: ProDev Architecture Series (Security Branch)
// Features: Dotenv integration, .gitignore best practices,
//           and centralized config utility mapping.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import VpnKeyIcon             from "@mui/icons-material/VpnKey";
import SettingsIcon           from "@mui/icons-material/Settings";
import SecurityIcon           from "@mui/icons-material/Security";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import VisibilityOffIcon      from "@mui/icons-material/VisibilityOff";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressEnvConfig() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Security · Module 22"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Environment <span>Configuration</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Hardcoding secrets like API keys and database URIs is a major security risk. Using environment variables ensures your credentials stay out of version control and allows for seamless transitions between dev, staging, and production.
                    </Typography>
                </Box>

                {/* 1. INSTALLATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Installation & .env Setup" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Install <code style={{color: BRAND.white}}>dotenv</code> and create a root-level file named <strong>.env</strong>.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "npm install dotenv", comment: "Install the variable loader" },
                    ]} />
                    <Typography sx={{ color: BRAND.white, fontSize: 13, mb: 1, mt: 2, fontWeight: 700 }}>📄 .env File Content</Typography>
                    <Box sx={{ p: 2, background: BRAND.codeBg, borderRadius: 2, border: `1px solid ${BRAND.border}` }}>
                        <Typography sx={{ fontFamily: "monospace", color: BRAND.codeText, fontSize: 13 }}>
                            PORT=4000<br/>
                            MONGO_URI=mongodb://localhost:27017/appdb<br/>
                            JWT_SECRET=super-secret-key-do-not-share
                        </Typography>
                    </Box>
                </Box>

                {/* 2. LOADING VARIABLES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="2. Loading into Express" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Call the config method at the very start of your entry point to populate <code style={{color: BRAND.white}}>process.env</code>.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "require('dotenv').config();", comment: "Call as early as possible" },
                        { cmd: "", comment: "" },
                        { cmd: "const express = require('express');", comment: "" },
                        { cmd: "const app = express();", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "const port = process.env.PORT || 3000;", comment: "Access via process.env" },
                        { cmd: "app.listen(port, () => console.log('Server Active'));", comment: "" },
                    ]} />
                </Box>

                {/* 3. SECURITY BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<VisibilityOffIcon />} label="3. Critical Security: .gitignore" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        <strong>Never</strong> commit your .env file to GitHub. Add it to your <strong>.gitignore</strong> immediately.
                    </Typography>
                    <Box sx={{ p: 2, background: BRAND.codeBg, borderRadius: 2, border: `1px solid #ff5f56` }}>
                        <Typography sx={{ fontFamily: "monospace", color: "#ff5f56", fontSize: 13 }}>
                            # .gitignore<br/>
                            .env<br/>
                            node_modules/
                        </Typography>
                    </Box>
                </Box>

                {/* 4. CONFIG UTILITY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsIcon />} label="4. The Centralized Config Pattern" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Instead of calling <code style={{color: BRAND.white}}>process.env</code> everywhere, create a config utility to map and validate your variables.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "// config/index.js", comment: "" },
                        { cmd: "require('dotenv').config();", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "module.exports = {", comment: "" },
                        { cmd: "  port: process.env.PORT || 3000,", comment: "" },
                        { cmd: "  mongoUri: process.env.MONGO_URI,", comment: "" },
                        { cmd: "  jwtSecret: process.env.JWT_SECRET", comment: "" },
                        { cmd: "};", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* MODULE OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Decouple sensitive credentials from application source code",
                            "Utilize the dotenv library to manage local environment variables",
                            "Prevent security leaks by configuring .gitignore correctly",
                            "Implement fallback values for optional configuration parameters",
                            "Design a centralized configuration module for better maintainability",
                            "Understand the difference between dev and production environment injection",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Always create a <code style={{ color: BRAND.goldBright }}>.env.example</code> file with empty values. Commit this to GitHub so other developers know which variables they need to define locally to get the project running.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
